import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 1000;
const PRELOAD_CHECK_INTERVAL = 60000; // Co 60 sekund sprawdzaj nowe treści

export default function Display() {
  const { screenId } = useParams();
  const [screenInfo, setScreenInfo] = useState(null);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [showLogo, setShowLogo] = useState(true);
  const [isPreloading, setIsPreloading] = useState(true);
  const [preloadProgress, setPreloadProgress] = useState(0);
  
  // Cache dla odporności na przerwy w internecie
  const lastValidStateRef = useRef(null);
  const failedAttemptsRef = useRef(0);
  const MAX_FAILED_ATTEMPTS = 10;
  
  // === WSZYSTKIE TREŚCI - lista załadowana na starcie ===
  const [allContent, setAllContent] = useState([]);
  const [activeContentId, setActiveContentId] = useState(null);
  const [activeContent, setActiveContent] = useState(null);
  const currentContentIdRef = useRef(null);
  
  // Refy do wszystkich video elementów
  const videoRefsMap = useRef({});

  // === Preładuj wszystkie treści ===
  const preloadAllContent = useCallback(async () => {
    console.log('🔄 Pobieram listę treści...');
    
    try {
      const response = await axios.get(`${API_URL}/display/screen/${screenId}/all-content`);
      const contentList = response.data.content_list || [];
      
      console.log(`📋 Znaleziono ${contentList.length} treści`);
      
      if (contentList.length === 0) {
        setIsPreloading(false);
        setShowLogo(true);
        return;
      }
      
      setAllContent(contentList);
      setIsPreloading(false);
      setShowLogo(false);
      
    } catch (error) {
      console.error('❌ Błąd pobierania treści:', error);
      setIsPreloading(false);
    }
  }, [screenId]);

  // === Sprawdzaj aktualny content ===
  const checkCurrentContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/display/screen/${screenId}/current`, {
        timeout: 5000
      });
      
      failedAttemptsRef.current = 0;
      
      if (response.data.content) {
        const newContentId = response.data.content.id;
        
        if (newContentId !== currentContentIdRef.current) {
          console.log(`🔄 Przełączam na: ${response.data.content.title} (ID: ${newContentId})`);
          
          // Zatrzymaj poprzednie video
          if (currentContentIdRef.current && videoRefsMap.current[currentContentIdRef.current]) {
            const oldVideo = videoRefsMap.current[currentContentIdRef.current];
            oldVideo.pause();
          }
          
          // Odtwórz nowe video
          if (videoRefsMap.current[newContentId]) {
            const newVideo = videoRefsMap.current[newContentId];
            newVideo.currentTime = 0;
            newVideo.play().catch(e => console.log('Play prevented:', e));
          }
          
          setActiveContentId(newContentId);
          setActiveContent(response.data.content);
          setCurrentSchedule(response.data.schedule);
          currentContentIdRef.current = newContentId;
          
          lastValidStateRef.current = {
            content: response.data.content,
            schedule: response.data.schedule,
            timestamp: Date.now()
          };
        }
      } else {
        if (!lastValidStateRef.current || failedAttemptsRef.current >= 3) {
          setShowLogo(true);
          currentContentIdRef.current = null;
        }
      }
    } catch (error) {
      failedAttemptsRef.current += 1;
      
      if (failedAttemptsRef.current >= MAX_FAILED_ATTEMPTS && !lastValidStateRef.current) {
        setShowLogo(true);
      }
    }
  }, [screenId]);

  // === Callback gdy video jest gotowe ===
  const handleVideoCanPlay = useCallback((contentId) => {
    console.log(`✅ Video gotowe: ${contentId}`);
    setPreloadProgress(prev => {
      const newProgress = Math.min(prev + Math.round(100 / Math.max(allContent.length, 1)), 100);
      if (newProgress >= 100) {
        setIsPreloading(false);
      }
      return newProgress;
    });
  }, [allContent.length]);

  // === Inicjalizacja ===
  useEffect(() => {
    const init = async () => {
      try {
        const response = await axios.get(`${API_URL}/display/screen/${screenId}/info`);
        setScreenInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch screen info:', error);
      }
      
      await preloadAllContent();
    };
    
    init();
    
    const contentInterval = setInterval(checkCurrentContent, POLL_INTERVAL);
    const preloadInterval = setInterval(preloadAllContent, PRELOAD_CHECK_INTERVAL);
    
    return () => {
      clearInterval(contentInterval);
      clearInterval(preloadInterval);
    };
  }, [screenId, preloadAllContent, checkCurrentContent]);

  // === Gdy mamy treści, zacznij sprawdzać aktualny content ===
  useEffect(() => {
    if (allContent.length > 0 && !isPreloading) {
      checkCurrentContent();
    }
  }, [allContent, isPreloading, checkCurrentContent]);

  // Render cropped portion for combined content
  const renderCombinedContent = (content) => {
    if (!screenInfo) return null;

    const position = screenInfo.position_in_group;
    const cropX = (position - 1) * 1080;

    if (content.type === 'video') {
      return (
        <div className="relative w-screen h-screen overflow-hidden bg-black">
          <video
            src={BACKEND_URL + content.file_url}
            className="absolute"
            style={{
              left: `-${cropX}px`,
              width: '4320px',
              height: '1920px',
              objectFit: 'cover',
              pointerEvents: 'none'
            }}
            autoPlay
            muted
            loop
            playsInline
            controls={false}
          />
        </div>
      );
    } else {
      return (
        <div className="relative w-screen h-screen overflow-hidden bg-black">
          <img
            src={BACKEND_URL + content.file_url}
            alt={content.title}
            className="absolute"
            style={{
              left: `-${cropX}px`,
              width: '4320px',
              height: '1920px',
              objectFit: 'cover'
            }}
          />
        </div>
      );
    }
  };

  // === LOADING SCREEN ===
  if (isPreloading && allContent.length === 0) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex flex-col items-center justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
          alt="AvePark" 
          className="w-1/3 max-w-xs mb-8"
        />
        <div className="text-white text-xl">Ładowanie...</div>
      </div>
    );
  }

  // === LOGO SCREEN ===
  if (showLogo || allContent.length === 0) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
          alt="AvePark" 
          className="w-1/2 max-w-lg"
        />
      </div>
    );
  }

  const isCombined = currentSchedule?.display_type === 'combined' && screenInfo?.position_in_group;

  if (isCombined && activeContent) {
    return renderCombinedContent(activeContent);
  }

  // === MAIN DISPLAY - WSZYSTKIE ELEMENTY RENDEROWANE NA STAŁE ===
  return (
    <div className="w-screen h-screen bg-black overflow-hidden relative">
      
      {/* === RENDERUJ WSZYSTKIE TREŚCI - STAŁE W DOM === */}
      {allContent.map((content) => {
        const isActive = content.id === activeContentId;
        const isVideo = content.type === 'video';
        const isMenu = content.type === 'menu' && content.editor_data;
        
        return (
          <div
            key={content.id}
            className="absolute inset-0"
            style={{
              visibility: isActive ? 'visible' : 'hidden',
              zIndex: isActive ? 10 : 1
            }}
          >
            {isVideo ? (
              <video
                ref={(el) => {
                  if (el) videoRefsMap.current[content.id] = el;
                }}
                src={BACKEND_URL + content.file_url}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                preload="auto"
                onCanPlayThrough={() => handleVideoCanPlay(content.id)}
                style={{ pointerEvents: 'none' }}
              />
            ) : isMenu ? (
              <img 
                src={content.editor_data.preview} 
                alt="Menu" 
                className="w-full h-full object-contain bg-zinc-900"
              />
            ) : content.file_url ? (
              <img
                src={BACKEND_URL + content.file_url}
                alt={content.title || ''}
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
        );
      })}

      {/* === CSS === */}
      <style>{`
        video::-webkit-media-controls,
        video::-webkit-media-controls-enclosure,
        video::-webkit-media-controls-panel,
        video::-webkit-media-controls-play-button,
        video::-webkit-media-controls-start-playback-button,
        video::-webkit-media-controls-overlay-play-button,
        video::-webkit-media-controls-overlay-enclosure {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        video {
          pointer-events: none !important;
        }
      `}</style>
    </div>
  );
}
